import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HSMark } from '../components/TrainerShell';
import { GlowBackground } from '../components/GlowBackground';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotHint, setShowForgotHint] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        // Route by role: client → client dashboard, trainer/admin → trainer dashboard
        if (result.role === 'client') {
          navigate('/client-dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(result.error || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'bg-[var(--glass-soft)] border-[var(--glass-soft-border)] rounded-2xl h-[52px] text-[var(--text-primary)] px-4';

  return (
    <div className="min-h-screen relative overflow-hidden bg-[var(--bg-primary)] text-[var(--text-primary)]" data-testid="login-page">
      <GlowBackground />

      <div className="app-content min-h-screen flex flex-col">
        <header className="px-5 sm:px-12 pt-6 sm:pt-9 flex items-center gap-3">
          <HSMark size={44} />
          <span className="text-sm text-[var(--text-muted)] tracking-wide">hstrener.com</span>
        </header>

        <main className="flex-1 flex flex-wrap items-center justify-center gap-10 lg:gap-20 px-4 sm:px-12 py-10">
          <section className="w-full max-w-md lg:max-w-lg flex flex-col gap-5 lg:gap-7">
            <p className="label-text text-[#C9A84C]">HStrener OS · System Staniszewskiego™</p>
            <h2 className="big-num text-[42px] sm:text-6xl lg:text-7xl">Silniej.<br />Sprawniej.<br />Zdrowiej.</h2>
            <p className="hidden sm:block text-[var(--text-muted)] font-light text-base leading-relaxed max-w-sm">
              Twój plan, treningi i postępy w jednym miejscu.
            </p>
          </section>

          <section className="glass w-full max-w-md rounded-[32px] p-7 sm:p-11 flex flex-col gap-7">
            <div>
              <h1 className="big-num text-4xl sm:text-5xl" data-testid="login-heading">Witaj</h1>
              <p className="mt-2 text-[var(--text-muted)] font-light">Zaloguj się do systemu</p>
            </div>

            {error && (
              <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20" data-testid="login-error">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div>
                <Label htmlFor="email" className="label-text text-[var(--text-muted)] mb-2.5 block">EMAIL</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className={inputCls} required autoComplete="email" data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="password" className="label-text text-[var(--text-muted)] mb-2.5 block">HASŁO</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className={inputCls} required autoComplete="current-password" data-testid="login-password-input" />
                <div className="mt-2.5 text-right">
                  <button type="button" onClick={() => setShowForgotHint(!showForgotHint)}
                    className="text-[var(--text-muted)] hover:text-[var(--text-primary)] text-xs" data-testid="forgot-password-toggle">
                    Zapomniałeś hasła?
                  </button>
                </div>
                {showForgotHint && (
                  <div className="mt-2 p-3 rounded-2xl glass-soft" data-testid="forgot-password-hint">
                    <p className="text-[var(--text-muted)] text-xs">
                      Skontaktuj się ze swoim trenerem — wyśle Ci nowy link, w którym ustawisz nowe hasło.
                    </p>
                  </div>
                )}
              </div>
              <button type="submit" disabled={loading}
                className="cta-primary w-full h-14 rounded-full text-[13px] font-bold tracking-[0.25em] uppercase disabled:opacity-60"
                data-testid="login-submit-button">
                {loading ? 'Proszę czekać...' : 'Zaloguj się'}
              </button>
            </form>

            <p className="text-xs text-[var(--text-muted)] text-center leading-relaxed">
              Konto zakłada trener — dostaniesz link do ustawienia hasła.
            </p>
          </section>
        </main>
      </div>
    </div>
  );
};